/**
 * Customers Page (Admin Only)
 * Manage customer accounts
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { fetchCustomers, setActiveCustomer } from '../../../store/slices/customerSlice';
import AppBar from '../../elements/AppBar';
import PageBody from '../../elements/PageBody';
import PageTitle from '../../elements/PageTitle';
import FilterSection from '../../elements/FilterSection';
import CustomerFormModal from '../../elements/CustomerFormModal';
import StatusChip from '../../elements/StatusChip';
import useProjectSelector from '../../../hooks/useProjectSelector';
import * as api from '../../../services/api';
import { resolveStatusTone, CAPABILITY_MAP } from '../../../utils/statusTone';

const CustomersPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { customerList, loading } = useAppSelector((state) => state.customer);
  const { ProjectSelectorComponent } = useProjectSelector();
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  
  // Form state
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Inline edit state for tracking/supply mode
  const [editingCell, setEditingCell] = useState(null); // { customerId, field }
  const [narrowingDialog, setNarrowingDialog] = useState(null); // { customer, field, newValue }

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchCustomers());
  };

  const handleViewAsCustomer = (customer) => {
    dispatch(setActiveCustomer(customer));
    setSnackbar({ 
      open: true, 
      message: `Now viewing as ${customer.name}. Navigating to map...`, 
      severity: 'success' 
    });
    setTimeout(() => navigate('/'), 1500);
  };

  const handleOpenAddModal = () => {
    setEditingCustomer(null);
    setFormError(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormError(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCustomer(null);
    setFormError(null);
  };

  const handleSubmitCustomer = async (data) => {
    setFormLoading(true);
    setFormError(null);
    
    try {
      if (editingCustomer) {
        // Update existing customer
        await api.updateCustomer(editingCustomer.id, data);
        setSnackbar({ 
          open: true, 
          message: `Customer "${data.name}" updated successfully`, 
          severity: 'success' 
        });
      } else {
        // Create new customer
        await api.createCustomer(data);
        setSnackbar({ 
          open: true, 
          message: `Customer "${data.name}" created successfully`, 
          severity: 'success' 
        });
      }
      
      handleCloseModal();
      dispatch(fetchCustomers());
    } catch (err) {
      console.error('Error saving customer:', err);
      setFormError(err.message || 'Failed to save customer');
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    
    setFormLoading(true);
    
    try {
      await api.deleteCustomer(customerToDelete.id);
      setSnackbar({ 
        open: true, 
        message: `Customer "${customerToDelete.name}" deleted successfully`, 
        severity: 'success' 
      });
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      dispatch(fetchCustomers());
    } catch (err) {
      console.error('Error deleting customer:', err);
      setSnackbar({ 
        open: true, 
        message: err.message || 'Failed to delete customer', 
        severity: 'error' 
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Inline edit handlers for tracking/supply mode
  const handleInlineEdit = (customerId, field) => {
    setEditingCell({ customerId, field });
  };

  const handleInlineChange = async (customer, field, newValue) => {
    setEditingCell(null);

    // Check if narrowing (more restrictive)
    const isNarrowing = (
      (field === 'trackingMode' && customer.trackingMode === 'hybrid' && newValue !== 'hybrid') ||
      (field === 'supplyMode' && customer.supplyMode === 'both' && newValue !== 'both')
    );

    if (isNarrowing) {
      setNarrowingDialog({ customer, field, newValue });
      return;
    }

    await saveInlineEdit(customer.id, field, newValue);
  };

  const saveInlineEdit = async (customerId, field, value) => {
    try {
      await api.updateCustomer(customerId, { [field]: value });
      dispatch(fetchCustomers());
      setSnackbar({ open: true, message: `${field} updated`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to update', severity: 'error' });
    }
  };

  const handleConfirmNarrowing = async () => {
    if (!narrowingDialog) return;
    const { customer, field, newValue } = narrowingDialog;
    setNarrowingDialog(null);
    await saveInlineEdit(customer.id, field, newValue);
  };

  // Ensure customerList is always an array, filter by search
  const customers = (customerList || []).filter((customer) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(q) ||
      customer.contact?.name?.toLowerCase().includes(q) ||
      customer.contact?.email?.toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            Customer Management
          </PageTitle>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddModal}
              sx={{ height: 44 }}
            >
              Add Customer
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <FilterSection>
          <TextField
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </FilterSection>

        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #4A4847' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Tracking</TableCell>
                <TableCell>Supply</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : customers.length > 0 ? (
                customers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {customer.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={customer.type} 
                        size="small"
                        color={customer.type === 'utility' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell
                      onClick={() => handleInlineEdit(customer.id, 'trackingMode')}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      {editingCell?.customerId === customer.id && editingCell?.field === 'trackingMode' ? (
                        <Select
                          value={customer.trackingMode || 'beacon'}
                          size="small"
                          autoFocus
                          onBlur={() => setEditingCell(null)}
                          onChange={(e) => handleInlineChange(customer, 'trackingMode', e.target.value)}
                          sx={{ minWidth: 100 }}
                        >
                          <MenuItem value="beacon">{CAPABILITY_MAP.beacon.label}</MenuItem>
                          <MenuItem value="manual">{CAPABILITY_MAP.manual.label}</MenuItem>
                          <MenuItem value="hybrid">{CAPABILITY_MAP.hybrid.label}</MenuItem>
                        </Select>
                      ) : (
                        <StatusChip
                          status={customer.trackingMode || 'beacon'}
                          domain="capability"
                          label={`${resolveStatusTone('capability', customer.trackingMode || 'beacon').label}${!customer.trackingMode ? ' (default)' : ''}`}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => handleInlineEdit(customer.id, 'supplyMode')}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      {editingCell?.customerId === customer.id && editingCell?.field === 'supplyMode' ? (
                        <Select
                          value={customer.supplyMode || 'both'}
                          size="small"
                          autoFocus
                          onBlur={() => setEditingCell(null)}
                          onChange={(e) => handleInlineChange(customer, 'supplyMode', e.target.value)}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="supply_only">Supply Only</MenuItem>
                          <MenuItem value="demand_only">Demand Only</MenuItem>
                          <MenuItem value="both">Both</MenuItem>
                        </Select>
                      ) : (
                        <StatusChip
                          status={customer.supplyMode || 'both'}
                          domain="supplyMode"
                          label={`${customer.supplyMode || 'both'}${!customer.supplyMode ? ' (default)' : ''}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {customer.contact?.name || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {customer.contact?.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={customer.status || 'Active'} 
                        size="small"
                        color={customer.status === 'Active' || customer.status === 'active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewAsCustomer(customer)}
                        title="View as this customer"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        title="Edit customer"
                        onClick={() => handleOpenEditModal(customer)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        title="Delete customer"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(customer)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No customers yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </PageBody>

      {/* Customer Form Modal */}
      <CustomerFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitCustomer}
        customer={editingCustomer}
        loading={formLoading}
        error={formError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{customerToDelete?.name}"? 
            This action cannot be undone and will affect all associated data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={formLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteCustomer} 
            color="error"
            variant="contained"
            disabled={formLoading}
          >
            {formLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Narrowing Confirmation Dialog (Iteration 12) */}
      <Dialog open={!!narrowingDialog} onClose={() => setNarrowingDialog(null)}>
        <DialogTitle>Confirm Mode Change</DialogTitle>
        <DialogContent>
          <Typography>
            Changing to a more restrictive mode will hide some surfaces.
            {narrowingDialog?.field === 'trackingMode' && (
              <> Projects with {narrowingDialog?.customer?.trackingMode === 'hybrid' ? 'beacon' : ''} data will still exist but won&apos;t appear in navigation.</>  
            )}
            {narrowingDialog?.field === 'supplyMode' && (
              <> Some count columns will be hidden from view.</>  
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNarrowingDialog(null)}>Cancel</Button>
          <Button
            onClick={handleConfirmNarrowing}
            variant="contained"
            color="warning"
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersPage;
